import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { api } from "../lib/api";
import { MaskedLines, FadeUp } from "../components/Reveal";
import { useContent } from "../context/ContentContext";
import { secStyle, homeHeroClass, focalStyle } from "../lib/layout";
import ReviewsRotator from "../components/ReviewsRotator";
import PicksCarousel from "../components/PicksCarousel";
import { coverSrc, heroSrcSet } from "../lib/images";

export default function Home() {
  const [picks, setPicks] = useState([]);
  const { content } = useContent();
  const heroStyle = focalStyle(content.hero.bg_focal);
  const heroImg = coverSrc(content.hero.bg_image, "hero");
  const hero = content.hero;
  const mani = content.manifesto;
  const picksMeta = content.picks;
  const fp = content.flawed_prophets;
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.28]);
  const titleY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    api.get("/picks").then((r) => setPicks(r.data)).catch((e) => console.error("Could not load the picks", e));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className={`relative ${homeHeroClass(hero.height)} flex flex-col justify-end overflow-hidden pt-16 lg:pt-20`}>
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 z-0">
          {heroImg && <img
            src={heroImg}
            srcSet={heroSrcSet(content.hero.bg_image)}
            sizes="100vw"
            alt="Vintage library"
            style={heroStyle}
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover contrast-105 hero-img gpu-layer"
          />}
          <div className="absolute inset-0 hero-overlay" />
          {(content.hero.bg_focal?.veil ?? 0) > 0 && (
            <div className="absolute inset-0 bg-black pointer-events-none" style={{ opacity: (content.hero.bg_focal.veil) / 100 }} />
          )}
        </motion.div>

        <motion.div style={{ y: titleY, opacity: fade }} className="relative z-10 max-w-[1600px] mx-auto w-full px-6 lg:px-12 pt-6 pb-16 lg:pb-24 hero-text">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
            className="overline mb-8 text-brass"
          >
            {hero.overline}
          </motion.p>
          <h1 className="font-serif font-light tracking-tighter leading-[0.86] text-vellum text-[clamp(3rem,13vw,11rem)]">
            <MaskedLines lines={[hero.title_line1, hero.title_line2]} delay={0.35} />
            <MaskedLines lines={[hero.highlight]} delay={0.6} lineClassName="italic text-brass" />
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9 }}
            className="mt-8 flex flex-col md:flex-row md:items-end justify-between gap-8"
          >
            <p className="max-w-md text-vellum-muted text-lg leading-relaxed">
              {hero.subtitle}
            </p>
            <div className="flex items-center gap-2 text-vellum-faint font-mono text-xs uppercase tracking-[0.2em]">
              <ArrowDown className="w-4 h-4 animate-bounce" /> Scroll to enter
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <section className="py-6 bg-ink-800" style={secStyle(content, "home_marquee")}>
        <Marquee speed={38} autoFill gradient={false}>
          {(content.marquee?.words || []).map((w, i) => (
            <span key={`${w}-${i}`} className="font-serif text-4xl lg:text-6xl font-light uppercase tracking-tight text-vellum/80 mx-8">
              {w} <span className="text-crimson mx-6">✦</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* MANIFESTO */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-20 lg:py-28" style={secStyle(content, "home_manifesto")} data-testid="manifesto">
        <FadeUp><p className="overline mb-12 text-crimson">{mani.label}</p></FadeUp>
        <div className="grid md:grid-cols-3 gap-10 lg:gap-14">
          {(mani.items || []).map((m, i) => (
            <FadeUp key={m.n || i} delay={i * 0.08}>
              <div className="border-t border-line pt-6">
                <div className="flex items-baseline gap-4 mb-4">
                  <span className="font-mono text-2xl text-crimson">{m.n}</span>
                  <h3 className="font-serif text-3xl font-light text-vellum tracking-tight">{m.t}</h3>
                </div>
                <p className="text-vellum-muted text-base leading-relaxed">{m.b}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* NAVIGATE — two prominent buttons */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 pb-24 lg:pb-32" style={secStyle(content, "home_buttons")} data-testid="nav-buttons">
        <div className="flex flex-col sm:flex-row gap-5">
          {(content.nav?.order || ["library", "curriculums"]).map((k) => {
            const to = `/${k}`;
            const t = content.nav?.[k]?.title || (k === "library" ? "The Library" : "Personal Curriculums");
            return (
            <Link
              key={to}
              to={to}
              data-testid={`nav-block-${k}`}
              className="group flex-1 flex items-center justify-between gap-6 px-8 py-6 lg:px-10 lg:py-7 rounded-sm transition-colors duration-300 bg-white text-[#0a0908] hover:bg-[#E8481C] hover:text-white"
            >
              <span className="font-serif text-3xl lg:text-4xl font-light tracking-tight">
                {t}
              </span>
              <ArrowUpRight className="w-7 h-7 shrink-0 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
            </Link>
            );
          })}
        </div>
      </section>

      {/* PICKS OF THE MONTH */}
      <section className="py-28 lg:py-40" style={secStyle(content, "home_picks")} data-testid="picks-section">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <FadeUp>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
              <div>
                <p className="overline mb-6 text-brass">{picksMeta.label}</p>
                <h2 className="font-serif text-5xl lg:text-7xl font-light tracking-tight text-vellum">
                  {picksMeta.heading_line1}<br /><span className="italic text-brass">{picksMeta.heading_line2}</span>
                </h2>
              </div>
              <p className="max-w-sm text-vellum-muted leading-relaxed">
                {picksMeta.blurb}
              </p>
            </div>
          </FadeUp>

          <PicksCarousel picks={picks} />
        </div>
      </section>

      {/* FLAWED PROPHETS */}
      <section className="relative overflow-hidden text-onaccent py-28 lg:py-40" style={{ ...secStyle(content, "home_book"), backgroundColor: "#8d271e" }} data-testid="flawed-prophets">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <FadeUp><p className="font-mono text-xs uppercase tracking-[0.25em] text-onaccent/70 mb-8">{fp.label}</p></FadeUp>
            <FadeUp delay={0.05}>
              <h2 className="font-serif text-6xl lg:text-8xl font-light leading-[0.9] tracking-tight">
                {fp.title_line1}<br /><span className="italic">{fp.title_line2}</span>
              </h2>
            </FadeUp>
            <FadeUp delay={0.12}>
              <p className="mt-8 max-w-lg text-lg text-onaccent/90 leading-relaxed">
                {fp.description}
              </p>
            </FadeUp>
            <FadeUp delay={0.18}>
              <div className="mt-12 flex flex-wrap gap-8">
                <a href={fp.amazon_url} target="_blank" rel="noreferrer" data-testid="fp-amazon"
                   className="group font-mono text-sm uppercase tracking-[0.2em] flex items-center gap-2 border-b-2 border-onaccent pb-2 hover:border-onaccent/50 transition-colors">
                  Buy on Amazon <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </a>
                <a href={fp.goodreads_url} target="_blank" rel="noreferrer" data-testid="fp-goodreads"
                   className="group font-mono text-sm uppercase tracking-[0.2em] flex items-center gap-2 border-b-2 border-transparent pb-2 text-onaccent/80 hover:text-onaccent hover:border-onaccent transition-colors">
                  View on Goodreads <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </a>
              </div>
            </FadeUp>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <FadeUp delay={0.1}>
              <div className="aspect-[3/4] overflow-hidden">
                <img src={coverSrc(fp.image, "detail")} loading="lazy" decoding="async"
                     alt="Flawed Prophets" className="w-full h-full object-cover contrast-105" />
              </div>
            </FadeUp>
            <FadeUp delay={0.18}>
              <ReviewsRotator />
            </FadeUp>
          </div>
        </div>
      </section>
    </div>
  );
}
